import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReportService } from '@core/services/report.service';
import { LanguageService } from '@core/services/language.service';
import { IReportSearch } from '@views/user/reports';
import { take } from 'rxjs';
import { ActivityAssignmentModalComponent } from './activity-assignment-modal/activity-assignment-modal.component';
import { IClassPlanning, IClassPlanningActivityResult, IClassPlanningDomain, IClassPlanningReport, IDomainLevel, IDomainMeasure, IDomainMeasureStudent } from '@core/interfaces/iclass-planning';
import { ActivityBankService } from '@core/services/activity-bank.service';
import { ToastService } from '@core/services/toast.service';
import { Router } from '@angular/router';
import { PermissionService } from '@core/services/permission.service';
import { Permission } from '@core/enums/permissions';
import { IRequiredReportFields } from '../interfaces/irequired-report-fields';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-class-planning',
    templateUrl: './class-planning.component.html',
    styleUrls: ['./class-planning.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ClassPlanningComponent implements OnInit {
  private reportService = inject(ReportService);
  private languageService = inject(LanguageService);
  private activityBankService = inject(ActivityBankService);
  private toast = inject(ToastService);
  private permissionService = inject(PermissionService);
  private router = inject(Router);

  private modal = inject(MatDialog);
  private translocoService = inject(TranslocoService);

  is2025: boolean = false; // Determines whether this is 2025 report search
  title: string = '';
  result: IClassPlanning | null = null;
  reportData: IClassPlanningReport | null = null;
  loading: boolean = false;
  searchValues: IReportSearch | null = null;
  accessActivityReport = this.permissionService.checkPermission(
    Permission.ActivitiesBankReport
  );
  requiredFields: IRequiredReportFields = {
    state: true,
    agency: true,
    globalRatingPeriod: true,
    site: true,
    classroom: true,
    ageGroup: true,
    requireMultipleRatingPeriods: false,
    requireRpInSameSchoolYear: true
  };

  domainClass(domainCode: string): string {
    return domainCode == "COG:MATH" || domainCode == "COG:SCI" || domainCode == "MATH" || domainCode == "SCI" ? "cog" : domainCode.toLowerCase();
  }

  getMeasureLevelStudents(measure: IDomainMeasure, levelCode: string): IDomainMeasureStudent[] {
    return measure.measureStudents[levelCode] || [];
  }

  constructor() {}

  ngOnInit(): void {
    this.is2025 = this.router.url.includes('2025-class-planning');
    this.title = this.translocoService.translate('reports.classPlanning.title', { year: this.is2025 ? '2025' : '2015' });
    if (this.is2025) {
      this.requiredFields.ageGroup = false;
    }
  }

  showReport(searchValues: IReportSearch) {
    this.loading = true;
    if (!searchValues.locale) {
      searchValues.locale = this.languageService.activeId();
    }

    this.searchValues = searchValues;

    const serviceMethod = (payload: IReportSearch) =>
      this.is2025
        ? this.reportService.generateClassPlanningReport2025(payload)
        : this.reportService.generateClassPlanningReport(payload);
    
    serviceMethod(searchValues)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.result = res;
          this.reportData = res.report;
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  downloadReport(searchValues: IReportSearch) {
    this.loading = true;
    
    const serviceMethod = (payload: IReportSearch) =>
      this.is2025
        ? this.reportService.generateClassPlanningReportPdf2025(payload)
        : this.reportService.generateClassPlanningReportPdf(payload);
    
    serviceMethod(searchValues)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          const file = new Blob([res], { type: 'application/pdf' });
          const fileUrl = URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = fileUrl;
          let fileName = `class_planning_report`;
          link.download = `${fileName.replace(/ /g, '_')}.pdf`;
          link.click();
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  handleClear(): void {
    this.reportData = null;
    this.result = null;
    this.searchValues = null;
  }

  onMeasureLevelSelect(measure: IDomainMeasure, level: IDomainLevel): void {
    if (this.searchValues && (this.searchValues.is2025AgeGradeSelected || this.is2025)) {
      this.searchValues.measureId = measure.id;
      var students = measure.measureStudents[level.levelCode];
      
      if (!students.length) return;
      
      this.searchValues.studentEnrollmentIds = students?.map(s => s.enrollmentId) || [];
      this.activityBankService 
      .getNextByMeasureEnrollmentClass(this.searchValues)
      .pipe(take(1))
      .subscribe((res: IClassPlanningActivityResult) => {
        if (res.hasSelectedActivities) this.toast.error(this.translocoService.translate('reports.classPlanning.activitiesAlreadyAssignedToast'));
        else {
          const modalRef = this.modal.open(ActivityAssignmentModalComponent, 
            { data: 
              { 
                activities: res.activities, 
                studentNames: res.students, 
                enrollmentIds: this.searchValues?.studentEnrollmentIds,
                measureName: measure.measureName,
                measureCode: measure.measureCode ,
                masterMeasureId: measure.masterMeasureId
              } 
            });

          modalRef.afterClosed().pipe(take(1)).subscribe((success: boolean) => {
            if (success && this.searchValues) this.showReport(this.searchValues);
          });
        }
      });
    }
  }

  goToActivityBank(): void {
    this.router.navigate(['/reports/activities-bank']);
  }
}
